import {
  Button,
  Flex,
  IconPlus,
  IconTrash,
  Radio,
  RadioGroup,
  Stack,
  csx,
  useRadioState,
} from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'

import { useIntl } from 'react-intl'

import type { TimeInterval } from 'typings'
import { useFormStateContext, useFormValidation } from '../../../../providers'
import { TimePicker } from '../../DateTime'
import { messages } from './messages'
import { validation } from './validation'

const SPECIFIC_TIME_INTERVALS = 'specific'
const ANYTIME = 'any'

export const EMPTY_INTERVAL = Object.freeze({
  start: undefined,
  end: undefined,
})

type Props = {
  name: string
}

export function TimeIntervals(props: Props) {
  const { name } = props
  const { formatMessage } = useIntl()
  const { form } = useFormStateContext()

  const timeIntervals: TimeInterval[] =
    useWatch({
      form,
      name,
    }) ?? []

  const state = useRadioState({
    value: timeIntervals.length ? SPECIFIC_TIME_INTERVALS : ANYTIME,
  })

  useFormValidation<TimeInterval[]>('time-intervals', {
    fieldName: name,
    validateFn: validation(name, state.value === SPECIFIC_TIME_INTERVALS),
  })

  const getFieldName = (index: number, type: 'start' | 'end') => {
    return `${name}.${index}.${type}`
  }

  const updateIntervals = (intervals: TimeInterval[]) => {
    form.setValue(name, intervals)
  }

  const removeInterval = (index: number) => {
    const newIntervals = [...timeIntervals]

    newIntervals.splice(index, 1)

    updateIntervals(newIntervals)
  }

  const addEmptyInterval = () => {
    const newIntervals = [...timeIntervals]

    newIntervals.push({ ...EMPTY_INTERVAL })

    updateIntervals(newIntervals)
  }

  const clearAllIntervals = () => {
    updateIntervals([])
    form.clearErrors(name)
  }

  return (
    <>
      <RadioGroup
        state={state}
        label={formatMessage(messages.timeIntervals)}
        className={csx({ marginY: '$space-5' })}
      >
        <Radio
          label={formatMessage(messages.anytime)}
          value={ANYTIME}
          onChange={clearAllIntervals}
          className={csx({ marginLeft: '0rem !important' })}
        />

        <Radio
          label={formatMessage(messages.specificIntervals)}
          value={SPECIFIC_TIME_INTERVALS}
          onChange={addEmptyInterval}
          className={csx({ marginLeft: '0rem !important' })}
        />
      </RadioGroup>

      {state.value === SPECIFIC_TIME_INTERVALS ? (
        <Stack
          space="$space-4"
          fluid
          className={csx({ paddingLeft: '$space-7' })}
        >
          {timeIntervals.map((_, index) => (
            <Flex
              key={index}
              className={csx({
                alignItems: 'flex-start',
                width: 'full',
                gap: '$space-3',
              })}
            >
              <div className={csx({ width: '4/12' })}>
                <TimePicker
                  name={getFieldName(index, 'start')}
                  label={formatMessage(messages.startTime)}
                  allowMinutes={false}
                />
              </div>

              <div className={csx({ width: '4/12' })}>
                <TimePicker
                  name={getFieldName(index, 'end')}
                  label={formatMessage(messages.endTime)}
                  allowMinutes={false}
                />
              </div>

              <Button
                variant="neutralTertiary"
                icon={<IconTrash />}
                size="large"
                aria-label={formatMessage(messages.removeInterval)}
                onClick={() => removeInterval(index)}
                className={csx({ height: 44, marginTop: '$space-6' })}
              />
            </Flex>
          ))}

          <div>
            <Button
              variant="neutralTertiary"
              icon={<IconPlus />}
              onClick={addEmptyInterval}
            >
              {formatMessage(messages.addInterval)}
            </Button>
          </div>
        </Stack>
      ) : null}
    </>
  )
}
